import {
  AUTH_BUILDER_ID,
  AUTH_EMAIL,
  AUTH_USER_TYPE,
  getAuthTokenClaims,
  getAuthTokenState,
  isAuthTokenExpired,
  isAuthTokenMalformed,
} from '@ctw/shared/utils/auth';
import {
  claimUrlsIncludingPermittedVariants,
  getAllowedAudiences,
  getAllowedIssuers,
} from '@ctw/shared/utils/auth-jwt';
import type { Env } from '@ctw/shared/utils/environments';

const REQUIRED_CLAIMS: Array<{ name: string; claimKey: string }> = [
  { name: 'builder_id', claimKey: AUTH_BUILDER_ID },
  { name: 'email', claimKey: AUTH_EMAIL },
  { name: 'user_type', claimKey: AUTH_USER_TYPE },
  { name: 'sub', claimKey: 'sub' },
  { name: 'exp', claimKey: 'exp' },
];

export interface AuthTokenDiagnostics {
  issuedAtSeconds: number | null;
  expiresAtSeconds: number | null;
  nowSeconds: number;
  skewSeconds: number | null;
  isExpired: boolean | null;
  isMalformed: boolean;
  issuer: string;
  isIssuerAllowed: boolean;
  audience: Array<string>;
  isAudienceAllowed: boolean;
  missingClaims: Array<string>;
}

/**
 * Claim-level diagnostics for an auth token, without verifying its signature
 * (no JWKS lookup is available client-side). `skewSeconds` is positive when
 * the token has already expired and negative while it is still valid.
 * `expiresAtSeconds`, `skewSeconds` and `isExpired` are null when the token
 * has no decodable `exp` claim, rather than treating a missing expiry as one.
 * `issuedAtSeconds` is likewise null when the token has no decodable `iat`
 * claim.
 */
export const getAuthTokenDiagnostics = (authToken: string, env: Env): AuthTokenDiagnostics => {
  const tokenState = getAuthTokenState(authToken);
  const claims = getAuthTokenClaims(authToken);
  const nowSeconds = Math.floor(Date.now() / 1000);
  const allowedIssuers = claimUrlsIncludingPermittedVariants(getAllowedIssuers(env));
  const allowedAudiences = claimUrlsIncludingPermittedVariants(getAllowedAudiences(env));
  const hasExpClaim = typeof claims.exp === 'number' && !Number.isNaN(claims.exp);
  const hasIatClaim = typeof claims.iat === 'number' && !Number.isNaN(claims.iat);

  return {
    issuedAtSeconds: hasIatClaim ? tokenState.iat : null,
    expiresAtSeconds: hasExpClaim ? tokenState.exp : null,
    nowSeconds,
    skewSeconds: hasExpClaim ? nowSeconds - tokenState.exp : null,
    isExpired: hasExpClaim ? isAuthTokenExpired(authToken) : null,
    isMalformed: isAuthTokenMalformed(authToken),
    issuer: tokenState.iss,
    isIssuerAllowed: allowedIssuers.includes(tokenState.iss),
    audience: tokenState.aud,
    isAudienceAllowed: tokenState.aud.some((audience) => allowedAudiences.includes(audience)),
    missingClaims: REQUIRED_CLAIMS.filter(({ claimKey }) => !claims[claimKey]).map(
      ({ name }) => name,
    ),
  };
};
